import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Camera } from 'lucide-react'
import { listAttachments, signedProofUrl } from '@/data/attachments'
import type { Attachment, ProofParent } from '@/data/attachments'
import { preparePhoto } from '@/attachments/compress'
import { describeQueueEntry } from '@/attachments/pure'
import { currentUserId } from '@/data/auth'
import { listQueue, processQueue, retryEntry, subscribeQueue } from '@/attachments/queue'
import type { QueueEntry } from '@/attachments/queue'
import { queueProofPhoto } from '@/attachments/receipts'
import { cn } from '@/lib/cn'

const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong. Please try again.')

/** Photos still waiting on this device for one expense; refreshes as the queue works. */
export function useQueuedPhotos(parentId: string): QueueEntry[] {
  const [items, setItems] = useState<QueueEntry[]>([])
  useEffect(() => {
    let alive = true
    const load = () =>
      void currentUserId()
        .then(async (me) => (await listQueue(parentId)).filter((x) => x.userId === me))
        .then((x) => alive && setItems(x))
        .catch(() => alive && setItems([]))
    load()
    const off = subscribeQueue(load)
    return () => {
      alive = false
      off()
    }
  }, [parentId])
  return items
}

function Thumb({ a, noun }: { a: Attachment; noun: string }) {
  const q = useQuery({
    queryKey: ['proof-url', a.thumbPath ?? a.storagePath],
    queryFn: () => signedProofUrl(a.thumbPath ?? a.storagePath),
    staleTime: 30 * 60 * 1000,
  })
  const open = async () => {
    // Open the tab NOW, inside the tap, and point it at the photo once the link is ready: phones block
    // a tab that is opened after waiting for the network.
    const tab = window.open('', '_blank')
    try {
      const url = await signedProofUrl(a.storagePath)
      if (tab) tab.location.href = url
      else window.location.assign(url)
    } catch {
      tab?.close() // the thumbnail still shows; opening can simply be tried again
    }
  }
  if (a.status !== 'ready') return null
  return (
    <button
      type="button"
      onClick={() => void open()}
      className="h-16 w-16 overflow-hidden rounded-control border border-line bg-page transition-colors hover:border-brand"
      aria-label={`Open ${noun} photo`}
    >
      {q.data ? <img src={q.data} alt={noun} className="h-full w-full object-cover" /> : <span className="text-micro text-muted">...</span>}
    </button>
  )
}

/**
 * The photos of one expense or remittance: saved ones, ones still uploading, and a way to add
 * more (tap to choose or take one; on a computer a photo can also be dropped on the box).
 * `noun` is what the photo is called on screen ('receipt', 'deposit slip').
 */
export function ProofPhotos({
  parent, departmentId, date, canAdd, noun = 'receipt',
}: { parent: ProofParent; departmentId: string | null; date: string; canAdd: boolean; noun?: string }) {
  const qc = useQueryClient()
  const saved = useQuery({ queryKey: ['attachments', parent.id], queryFn: () => listAttachments(parent) })
  const queued = useQueuedPhotos(parent.id)
  const input = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [dragging, setDragging] = useState(false)

  // When the queue finishes a photo, show it from the server.
  useEffect(() => subscribeQueue(() => void qc.invalidateQueries({ queryKey: ['attachments', parent.id] })), [qc, parent.id])

  const pick = async (files: FileList | File[] | null) => {
    if (!files || files.length === 0) return
    setBusy(true)
    setError('')
    try {
      for (const f of Array.from(files)) {
        const photo = await preparePhoto(f)
        await queueProofPhoto(photo, { departmentId, parent, date })
      }
    } catch (e) {
      setError(errText(e))
    } finally {
      setBusy(false)
      if (input.current) input.current.value = ''
    }
  }

  const ready = (saved.data ?? []).filter((a) => a.status === 'ready')
  if (!canAdd && ready.length === 0 && queued.length === 0) return null

  return (
    <div className="mt-2">
      {(ready.length > 0 || queued.length > 0) && (
        <div className="flex flex-wrap items-center gap-2" aria-label={`${noun} photos`}>
          {ready.map((a) => <Thumb key={a.id} a={a} noun={noun} />)}
        </div>
      )}
      {queued.map((q) => (
        <p key={q.id} className="mt-1 flex flex-wrap items-center gap-2 text-body text-wait" role="status">
          <span>Photo: {describeQueueEntry(q)}</span>
          <button
            type="button"
            onClick={() => void (q.status === 'failed' ? retryEntry(q.id) : processQueue({ force: true }))}
            className="inline-flex min-h-[44px] items-center rounded-control px-3 text-body font-medium text-brand hover:bg-neutral-tint"
          >
            {q.status === 'failed' ? 'Try again' : 'Try now'}
          </button>
        </p>
      ))}
      {canAdd && (
        <div
          onDragOver={(e) => {
            e.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDragging(false)
            void pick(e.dataTransfer.files)
          }}
          className={cn('mt-2 rounded-control border border-dashed px-3 py-1 transition-colors', dragging ? 'border-brand bg-brand-tint' : 'border-line-strong')}
        >
          <input
            ref={input}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            className="sr-only"
            aria-label={`Add a ${noun} photo`}
            onChange={(e) => void pick(e.target.files)}
          />
          <button
            type="button"
            disabled={busy}
            onClick={() => input.current?.click()}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-control text-body font-medium text-brand hover:underline disabled:opacity-50"
          >
            <Camera size={16} aria-hidden />
            {busy ? 'Preparing photo...' : ready.length + queued.length === 0 ? `Add a ${noun} photo` : 'Add another photo'}
          </button>
          <span className="hidden text-label text-muted md:inline"> or drop a photo here</span>
        </div>
      )}
      {error && <p role="alert" className="mt-1 text-body text-bad">{error}</p>}
    </div>
  )
}
